// Shared app-window behaviour for Plasmic-designed windows (OP1WebAW,
// OP1WebWindow, ...). Any Plasmic window component with the same named parts
// (shell, header, awHeaderSlotWrapp) and props (windowId, starting*,
// maximized*, onClickCloseButton, onClickMaximizeButton, isActive /
// isMaximized variants) gets the full behaviour via createAppWindow().
//
// Originally written for OP1WebAW: the app window. Its look (shell, header, icon, buttons, slots,
// `isActive` variant) is designed in Plasmic; this owned wrapper adds the
// behaviour of the OP1WebFrameWindow code component:
//   - position / size / stacking / open-minimized come from Desktop state
//   - drag: ONLY from the header slot (where AWHeaderContent sits), but it
//     moves the whole window; buttons/links/inputs inside it stay clickable
//   - resize from the bottom-right corner
//   - focus on any pointer-down; `isActive` variant follows focus
//   - the close button closes the window (any Studio handler still runs)
//   - the header is kept inside the OP1WebWindowManagerSurface
// Without a `windowId` it renders exactly as designed (no behaviour).
//
// This file is owned by us: `plasmic sync` never overwrites it.
import * as React from "react";
import { useOptionalDesktopState } from "./DesktopStateProvider";
import { OP1WebFrameWindowProvider } from "./OP1WebFrameWindowContext";
import "./Op1WebAw.css";

const { useCallback, useEffect, useRef } = React;

const MIN_WIDTH = 240;
const MIN_HEIGHT = 160;
// Interactive things inside the header must keep working instead of dragging.
const NO_DRAG = "button, a, input, textarea, select, [data-no-drag]";

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

function setRef(ref, node) {
  if (typeof ref === "function") ref(node);
  else if (ref) ref.current = node;
}

function getSurface(el) {
  if (!el) return null;
  return el.closest("[data-window-manager-surface]") || el.offsetParent;
}

// Keeps the whole header inside the surface (the body may hang outside).
function clampHeader({ x, y, width, headerBottom, surfaceWidth, surfaceHeight }) {
  return {
    x: clamp(x, 0, Math.max(0, surfaceWidth - width)),
    y: clamp(y, 0, Math.max(0, surfaceHeight - headerBottom)),
  };
}

const outerBase = { position: "absolute", display: "flex", boxSizing: "border-box" };
// The designed shell fills the positioned outer box (its designed
// width/height are replaced by the window's size from Desktop state).
const shellFill = { width: "100%", height: "100%" };
const resizeHandleStyle = {
  position: "absolute",
  right: 0,
  bottom: 0,
  width: 14,
  height: 14,
  cursor: "default",
  touchAction: "none",
  zIndex: 1,
};
// Build review NOV.01.2026: the arrow cursor everywhere, always (also while
// dragging/resizing). Op1WebAw.css forces it on everything inside the window.
const dragZoneStyle = { cursor: "default", touchAction: "none", userSelect: "none" };

// Studio number props may arrive as numbers or numeric strings.
const toNum = (v) => {
  if (v === undefined || v === null || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

// Props/variants that exist on the Plasmic component only after they're
// added in Studio and synced; until then they're simply not passed (so they
// never leak onto the DOM as unknown attributes).

// Maximized size = percentages of the surface (screen), centered.
function maximizedRect(surface, widthPct, heightPct) {
  const sw = surface?.clientWidth || window.innerWidth;
  const sh = surface?.clientHeight || window.innerHeight;
  const pw = clamp(widthPct ?? 100, 10, 100);
  const ph = clamp(heightPct ?? 100, 10, 100);
  const width = Math.round((sw * pw) / 100);
  const height = Math.round((sh * ph) / 100);
  return {
    x: Math.round((sw - width) / 2),
    y: Math.round((sh - height) / 2),
    width,
    height,
  };
}

export function createAppWindow(PlasmicWindow, displayName) {
const hasArg = (name) =>
  (PlasmicWindow.internalArgProps || []).includes(name);
const hasVariant = (name) =>
  (PlasmicWindow.internalVariantProps || []).includes(name);

function AppWindow_(
  {
    windowId,
    onClickCloseButton,
    onClickMaximizeButton,
    isMaximized: isMaximizedProp,
    maximizedWidth,
    maximizedHeight,
    isActive,
    className,
    startingWidth,
    startingHeight,
    startingX,
    startingY,
    // Old names (before build review NOV.01.2026), still honoured until the
    // props are renamed in Studio and synced.
    defaultWidth,
    defaultHeight,
    defaultX,
    defaultY,
    ...props
  },
  ref
) {
  const ctx = useOptionalDesktopState();
  const win = windowId && ctx ? ctx.state.windows[windowId] : undefined;
  const actions = ctx?.actions;


  const outerEl = useRef(null);
  const headerEl = useRef(null);
  const dragState = useRef(null);
  const resizeState = useRef(null);
  const winRef = useRef(win);
  winRef.current = win;
  const hasWin = !!win;
  const maxW = toNum(maximizedWidth);
  const maxH = toNum(maximizedHeight);
  const maxPctRef = useRef({ maxW, maxH });
  maxPctRef.current = { maxW, maxH };

  // Per-instance opening size/position (Studio props startingWidth /
  // startingHeight / startingX / startingY). Overrides windowRegistry.js;
  // never undoes a drag/resize the user already did.
  // startingX / startingY are offsets from the CENTER of the surface (screen):
  // 0/0 = centered, positive X = right, positive Y = down.
  const dW = toNum(startingWidth) ?? toNum(defaultWidth);
  const dH = toNum(startingHeight) ?? toNum(defaultHeight);
  const dX = toNum(startingX) ?? toNum(defaultX);
  const dY = toNum(startingY) ?? toNum(defaultY);
  const hasWinForDefaults = !!win;
  useEffect(() => {
    if (!windowId || !actions) return;
    if ([dW, dH, dX, dY].every((v) => v === undefined)) return;
    const cur = winRef.current;
    const width = dW ?? cur?.width ?? 420;
    const height = dH ?? cur?.height ?? 480;
    let x;
    let y;
    if (dX !== undefined || dY !== undefined) {
      const surface = getSurface(outerEl.current);
      const sw = surface?.clientWidth || window.innerWidth;
      const sh = surface?.clientHeight || window.innerHeight;
      if (dX !== undefined) x = Math.round((sw - width) / 2 + dX);
      if (dY !== undefined) y = Math.round((sh - height) / 2 + dY);
    }
    actions.setWindowDefaults(windowId, { width: dW, height: dH, x, y });
  }, [actions, windowId, dW, dH, dX, dY, hasWinForDefaults]);

  const focus = useCallback(() => {
    if (win && actions && !win.isFocused) actions.focusWindow(windowId);
  }, [win, actions, windowId]);

  const measure = useCallback(() => {
    const surface = getSurface(outerEl.current);
    const outer = outerEl.current;
    const h = headerEl.current;
    let headerBottom = 0;
    if (outer && h) {
      const o = outer.getBoundingClientRect();
      const r = h.getBoundingClientRect();
      headerBottom = r.bottom - o.top;
    }
    return {
      surfaceWidth: surface ? surface.clientWidth : 0,
      surfaceHeight: surface ? surface.clientHeight : 0,
      headerBottom,
    };
  }, []);

  // Re-clamp when the surface changes size (and once on mount).
  useEffect(() => {
    if (!hasWin || !actions) return undefined;
    const surface = getSurface(outerEl.current);
    if (!surface || typeof ResizeObserver === "undefined") return undefined;
    const reclamp = () => {
      const cur = winRef.current;
      if (!cur) return;
      const { surfaceWidth, surfaceHeight, headerBottom } = measure();
      if (!surfaceWidth || !surfaceHeight) return;
      if (cur.isMaximized) {
        // Keep a maximized window at its percentage size when the screen
        // changes size.
        const { maxW: w, maxH: h } = maxPctRef.current;
        const r = maximizedRect(surface, w, h);
        if (r.x !== cur.x || r.y !== cur.y || r.width !== cur.width || r.height !== cur.height) {
          actions.maximizeWindow(windowId, r);
        }
        return;
      }
      const next = clampHeader({ ...cur, headerBottom, surfaceWidth, surfaceHeight });
      if (next.x !== cur.x || next.y !== cur.y) {
        actions.moveWindow(windowId, next.x, next.y);
      }
    };
    const observer = new ResizeObserver(reclamp);
    observer.observe(surface);
    return () => observer.disconnect();
  }, [actions, windowId, hasWin, measure]);

  // ---- drag (header slot only) ----
  const onDragDown = (event) => {
    if (!win || event.button !== 0) return;
    if (win.isMaximized) return; // maximized windows don't move
    if (event.target.closest(NO_DRAG)) return;
    focus();
    dragState.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: win.x,
      originY: win.y,
      width: win.width,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
      };
  const onDragMove = (event) => {
    const d = dragState.current;
    if (!d) return;
    const x = d.originX + event.clientX - d.startX;
    const y = d.originY + event.clientY - d.startY;
    const { surfaceWidth, surfaceHeight, headerBottom } = measure();
    const next =
      surfaceWidth && surfaceHeight
        ? clampHeader({ x, y, width: d.width, headerBottom, surfaceWidth, surfaceHeight })
        : { x: Math.max(0, x), y: Math.max(0, y) };
    actions.moveWindow(windowId, next.x, next.y);
  };
  const onDragEnd = (event) => {
    if (!dragState.current) return;
    dragState.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // already released
    }
  };

  // ---- resize (bottom-right corner) ----
  const onResizeDown = (event) => {
    if (!win || win.isMaximized) return;
    event.stopPropagation();
    focus();
    resizeState.current = {
      startX: event.clientX,
      startY: event.clientY,
      originWidth: win.width,
      originHeight: win.height,
      x: win.x,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onResizeMove = (event) => {
    const r = resizeState.current;
    if (!r) return;
    const { surfaceWidth } = measure();
    // Width can't pass the surface's right edge, or the header would leave.
    const maxWidth = surfaceWidth ? Math.max(MIN_WIDTH, surfaceWidth - r.x) : Infinity;
    actions.resizeWindow(
      windowId,
      clamp(r.originWidth + event.clientX - r.startX, MIN_WIDTH, maxWidth),
      Math.max(MIN_HEIGHT, r.originHeight + event.clientY - r.startY)
    );
  };
  const onResizeEnd = (event) => {
    resizeState.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // already released
    }
  };

  // No window behind it (no windowId, no provider, or unknown id):
  // render exactly as designed.
  if (!win) {
    const designProps = {};
    if (hasVariant("isMaximized")) designProps.isMaximized = isMaximizedProp;
    if (hasArg("onClickMaximizeButton")) {
      designProps.onClickMaximizeButton = onClickMaximizeButton;
    }
    return (
      <PlasmicWindow
        shell={{ ref }}
        className={className}
        isActive={isActive}
        onClickCloseButton={onClickCloseButton}
        {...designProps}
        {...props}
      />
    );
  }

  const isVisible = win.isOpen && !win.isMinimized;

  const handleClose = (...args) => {
    onClickCloseButton?.(...args);
    actions.closeWindow(windowId);
  };

  // Maximize <-> restore. The `isMaximized` variant swaps the pictogram
  // (maximize -> minimize) in the design.
  const handleMaximize = (...args) => {
    onClickMaximizeButton?.(...args);
    focus();
    if (win.isMaximized) {
      actions.unmaximizeWindow(windowId);
    } else {
      const surface = getSurface(outerEl.current);
      actions.maximizeWindow(windowId, maximizedRect(surface, maxW, maxH));
    }
  };

  const liveProps = {};
  if (hasVariant("isMaximized")) liveProps.isMaximized = !!win.isMaximized;
  if (hasArg("onClickMaximizeButton")) {
    liveProps.onClickMaximizeButton = handleMaximize;
  }

  return (
    <OP1WebFrameWindowProvider value={{ windowId }}>
      <div
        ref={(node) => {
          outerEl.current = node;
          setRef(ref, node);
        }}
        className={className}
        data-window-id={windowId}
        data-op1-aw=""
        data-focused={win.isFocused ? "true" : "false"}
        data-maximized={win.isMaximized ? "true" : "false"}
        style={{
          ...outerBase,
          visibility: isVisible ? "visible" : "hidden",
          pointerEvents: isVisible ? "auto" : "none",
          left: win.x,
          top: win.y,
          width: win.width,
          height: win.height,
          zIndex: win.zIndex,
        }}
        onPointerDownCapture={focus}
      >
        <PlasmicWindow
          {...props}
          {...liveProps}
          isActive={win.isFocused && !win.isMinimized}
          onClickCloseButton={handleClose}
          shell={{ style: shellFill }}
          header={{ ref: headerEl }}
          awHeaderSlotWrapp={{
            style: dragZoneStyle,
            onPointerDown: onDragDown,
            onPointerMove: onDragMove,
            onPointerUp: onDragEnd,
            onPointerCancel: onDragEnd,
          }}
        />
        {!win.isMaximized && (
        <div
          style={resizeHandleStyle}
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeEnd}
          onPointerCancel={onResizeEnd}
        />
        )}
      </div>
    </OP1WebFrameWindowProvider>
  );
}

const AppWindow = React.forwardRef(AppWindow_);
AppWindow.displayName = displayName;
return AppWindow;
}
